import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppState, PermissionsAndroid, Platform } from 'react-native';

/**
 * The microphone, asked for before the first call rather than during it.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS ASKED BEFORE AND NOT DURING
 * ---------------------------------------------------------------------------
 * The vendor's own checklist says to request microphone permission before the
 * first call, not during (§8), and the reason is worth writing down: a
 * permission dialog that appears *while a phone is ringing* is answered by
 * reflex. People tap whatever dismisses the box fastest so they can take the
 * call, and on Android two reflexive dismissals turn into `never_ask_again` -
 * a state this app cannot recover from at all. The only way back is Settings.
 *
 * This used to run at the top of `startCall` and at the top of `acceptCall`,
 * and nowhere else. `acceptCall` is exactly the moment the note above warns
 * about: the handset is ringing, the overlay is up, and the dialog lands on
 * top of both.
 *
 * The fix for that was to mount `useMicrophonePermission` in
 * `VoiceCallProvider` and let MOUNTING be the ask - which moved the prompt
 * far ahead of any call, but moved it too far. The provider wraps the whole
 * app, so the prompt fired on the first screen after sign-in: somebody
 * browsing a catalogue and adding a car wash to their basket was asked to
 * record audio, with nothing on screen to explain why. A permission asked for
 * out of context is a permission that gets denied, and two denials are
 * `never_ask_again`.
 *
 * So the ask is armed but not fired. `useMicrophonePermission` OBSERVES the
 * permission on mount - a `check`, which draws nothing - and the prompt is
 * raised by `request()`, called from the places a microphone is actually
 * about to be used: pressing Call, and the Call button's own pre-flight. The
 * `check` on mount is what keeps `granted`/`blocked` accurate for a button
 * that wants to know in advance.
 *
 * ---------------------------------------------------------------------------
 * A REFUSAL IS REMEMBERED
 * ---------------------------------------------------------------------------
 * After `never_ask_again`, `PermissionsAndroid.request` returns a refusal
 * immediately and draws nothing at all. Calling it again is not persistence,
 * it is a no-op that merely looks like one - so the answer is recorded and
 * nothing asks again for the life of the process. Getting past it needs system
 * settings, which is a screen's decision and not this module's.
 *
 * The memory is deliberately a module variable rather than storage: it is only
 * ever an optimisation over what `check` already knows, and somebody who goes
 * to Settings and grants it must not be told "blocked" for ever afterwards.
 * Every path checks the real permission first, and a grant clears the memory.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS HAND-ROLLED
 * ---------------------------------------------------------------------------
 * This app has no `react-native-permissions`, deliberately - `LocationScreen`
 * and `services/fix.ts` both ask the platform directly, and adding a
 * permissions library for one more string would be a dependency to carry for
 * a `check`-then-`request` pair. This mirrors those two exactly.
 *
 * iOS has no request call. The prompt fires the first time audio is captured,
 * using `NSMicrophoneUsageDescription` from Info.plist, so there is nothing
 * for this to do there but say yes and let the engine trigger it.
 */

export type MicrophoneOutcome =
  | 'granted'
  /** Refused this time. Asking again later is allowed and may work. */
  | 'denied'
  /** Refused permanently. Only Settings can undo it - say so, do not re-ask. */
  | 'blocked';

/**
 * The sentence shown when a call cannot go ahead for want of a microphone.
 *
 * Two different sentences because the two outcomes need different actions from
 * the customer, and telling somebody to "allow it when asked" when they will
 * never be asked again is the kind of instruction that gets an app uninstalled.
 */
export const microphoneCopy = (outcome: MicrophoneOutcome): string =>
  outcome === 'blocked'
    ? 'Microphone access is turned off. Turn it on in Settings > Apps > Homingo > Permissions to make calls.'
    : 'Homingo needs your microphone to place a call.';

/**
 * Whether the microphone is already granted, without asking.
 *
 * The truth, every time - a grant can be revoked from Settings while the app
 * is open, and a check is the only thing that finds that out. Everything in
 * this file starts here, and so may anything outside it that wants to know in
 * advance: a settings row, or a Call button that would rather not surprise
 * somebody.
 */
export const hasMicrophone = async (): Promise<boolean> => {
  if (Platform.OS !== 'android') {
    return true;
  }
  try {
    return await PermissionsAndroid.check(
      PermissionsAndroid.PERMISSIONS.RECORD_AUDIO,
    );
  } catch {
    return false;
  }
};

/**
 * Android has stopped drawing the dialog for this process.
 *
 * See the note at the top. Cleared the moment a `check` comes back true, which
 * is what makes "went to Settings, granted it, came back" work.
 */
let refusedOutright = false;

/**
 * The ask currently in flight, if there is one.
 *
 * Two concurrent `PermissionsAndroid.request` calls are not two dialogs:
 * Android answers the second one immediately, as a denial, which is how a
 * mounted permission hook and an in-call re-verify racing each other can
 * manufacture a refusal nobody made. Sharing the promise means the second
 * caller waits for the first answer instead of poisoning it.
 */
let asking: Promise<MicrophoneOutcome> | null = null;

/**
 * Checks, and asks only if it is not held and still askable.
 *
 * Never throws - the caller is in the middle of placing or answering a call
 * and has no useful way to handle an exception from a permission check. A
 * platform that refuses to show the dialog at all is reported as `denied`,
 * which is the same outcome from where the call is standing.
 */
export const ensureMicrophone = async (): Promise<MicrophoneOutcome> => {
  if (Platform.OS !== 'android') {
    return 'granted';
  }

  /*
   * Checked before requesting, so a customer who has already granted it never
   * sees a dialog again. `request` on an already-granted permission returns
   * immediately on most builds, but not all - and a flash of a system dialog
   * on every call is the sort of thing people report as the app "asking
   * permission over and over".
   */
  if (await hasMicrophone()) {
    refusedOutright = false;
    return 'granted';
  }

  /*
   * Already refused outright. Asking again draws nothing and returns a denial,
   * so this reports the state it is actually in rather than pretending a
   * dialog was shown - `microphoneCopy` has a different sentence for it, and
   * that sentence is the only useful thing left to say.
   */
  if (refusedOutright) {
    return 'blocked';
  }

  if (asking) {
    return asking;
  }

  asking = (async (): Promise<MicrophoneOutcome> => {
    try {
      const answer = await PermissionsAndroid.request(
        PermissionsAndroid.PERMISSIONS.RECORD_AUDIO,
      );

      if (answer === PermissionsAndroid.RESULTS.GRANTED) {
        refusedOutright = false;
        return 'granted';
      }

      /*
       * Logged as well as returned, the same way `LocationScreen` logs a
       * refused location. `never_ask_again` in particular leaves no trace
       * anywhere else: the dialog simply stops appearing, and without this
       * line the only symptom is a Call button that seems to do nothing.
       */
      if (answer === PermissionsAndroid.RESULTS.NEVER_ASK_AGAIN) {
        refusedOutright = true;
        console.warn(
          '[voice] microphone permission never_ask_again - calls are not ' +
            'possible until it is granted in Settings > Apps > Homingo > Permissions.',
        );
        return 'blocked';
      }

      console.warn('[voice] microphone permission denied - the call cannot be placed.');
      return 'denied';
    } catch {
      // The dialog could not be shown. Indistinguishable from a refusal here,
      // and handled the same way by the caller.
      return 'denied';
    }
  })().finally(() => {
    asking = null;
  });

  return asking;
};

export type MicrophoneState = {
  granted: boolean;
  /** Android has stopped drawing the dialog. Nothing asks again while true. */
  blocked: boolean;
  /** Check, and ask if it is not held and still askable. Never throws. */
  request: () => Promise<MicrophoneOutcome>;
};

/**
 * Mounting this OBSERVES the permission. It does not ask for it.
 *
 * The distinction is the whole point of this hook. `VoiceCallProvider` wraps
 * the entire app, so anything this does on mount happens on whatever screen
 * the customer is looking at - a catalogue, a basket, a booking. Asking there
 * is what produced "why does the app want to record audio to add a car wash
 * to my cart", and an out-of-context prompt is one people are right to deny.
 *
 * So mount performs a `check`, which draws nothing, and the prompt is left to
 * `request()` - called from the Call button and from `startCall`, where the
 * customer has just asked for the thing the microphone is for. §8's "before
 * the first call, not during" is still honoured: the Call button asks before
 * dialling, and `IncomingCallOverlay` asks from its own control rather than
 * over a ringing handset.
 *
 * Two things are re-read rather than remembered, and both matter:
 *
 *  - **"Only this time" dies with the process.** Re-reading on every mount is
 *    what makes the next launch behave correctly.
 *  - **It can change outside the app.** Somebody can grant it from Settings
 *    while this app sits in the background, so the `AppState` listener
 *    re-reads on every return rather than trusting what it last knew. That is
 *    the only route out of `blocked`, since nothing here will ask again.
 *
 * @param enabled hold off until there is a reason. The provider passes "this
 *   handset has an account", because a signed-out app can neither place a call
 *   nor be rung for one.
 */
export const useMicrophonePermission = (enabled = true): MicrophoneState => {
  const [outcome, setOutcome] = useState<MicrophoneOutcome | null>(
    Platform.OS === 'android' ? null : 'granted',
  );

  const request = useCallback(async (): Promise<MicrophoneOutcome> => {
    const next = await ensureMicrophone();
    setOutcome(next);
    return next;
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let alive = true;
    /*
     * A `check`, never a `request` - see the note above. This reads the
     * permission without drawing anything, which is what lets a Call button
     * know in advance whether it needs to ask.
     *
     * `blocked` is deliberately NOT inferred here. `check` cannot tell a
     * plain denial from `never_ask_again`, and only `ensureMicrophone` - via
     * a real `request` - ever learns which one it is. Reporting a not-yet-
     * granted permission as merely not-granted keeps the Call button offering
     * to ask, which is the honest state before anybody has been asked.
     */
    const observe = () => {
      hasMicrophone().then(granted => {
        if (alive) {
          setOutcome(current =>
            granted ? 'granted' : current === 'granted' ? null : current,
          );
        }
      });
    };

    observe();
    const subscription = AppState.addEventListener('change', next => {
      if (next === 'active') {
        observe();
      }
    });

    return () => {
      alive = false;
      subscription.remove();
    };
  }, [enabled]);

  /* Memoised, and not as a micro-optimisation: `VoiceCallProvider` puts this
     into `useCallback` dependency arrays, and a fresh object every render would
     give `startCall` and `acceptCall` a new identity every render - which in
     turn would tear down and re-register the whole push subscription, every
     render, for the life of the app. */
  return useMemo(
    () => ({
      granted: outcome === 'granted',
      blocked: outcome === 'blocked',
      request,
    }),
    [outcome, request],
  );
};
